import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateTime } from '../lib/format'
import { useNotifications, useReadAllNotifications, useReadNotification, type NotificationItem, type NotificationKind } from '../features/notifications/api'

const tones: Record<NotificationKind, string> = {
  StockRequested: 'bg-dpl-accent/15 text-[#8A5A00]',
  StockRequestApproved: 'bg-dpl-success/10 text-dpl-success',
  StockRequestRejected: 'bg-dpl-danger/10 text-dpl-danger',
  StockOnTheWay: 'bg-dpl-primary/10 text-dpl-primary',
  StockArrived: 'bg-dpl-success/10 text-dpl-success',
  DeliveryAssigned: 'bg-violet-100 text-violet-700',
  StockHandedOver: 'bg-dpl-accent/15 text-[#8A5A00]',
  // Not red: nothing has gone wrong. Somebody has to telephone a customer, which is amber work.
  HoldReleased: 'bg-dpl-accent/15 text-[#8A5A00]',
}

/** A glyph per kind: asking, answered, on the road, arrived. */
function Glyph({ kind }: { kind: NotificationKind }) {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {kind === 'StockRequested' && <><path d="M10 4v8" /><path d="M6.5 8.5 10 12l3.5-3.5" /><path d="M3.5 15.5h13" /></>}
      {kind === 'StockRequestApproved' && <path d="m4.5 10.5 3.5 3.5 7.5-8" />}
      {kind === 'StockRequestRejected' && <><path d="m5.5 5.5 9 9M14.5 5.5l-9 9" /></>}
      {kind === 'DeliveryAssigned' && <><circle cx="10" cy="6" r="2.5" /><path d="M5 17a5 5 0 0 1 10 0" /><path d="m13.5 11.5 2 2 3-3.5" /></>}
      {kind === 'StockHandedOver' && <><path d="M3 11.5 7 9l3 1.5 4-2.5 3 1.5v4l-3 1.5-4-1-3 1.5-4-1.5z" /></>}
      {kind === 'StockOnTheWay' && <><path d="M2.5 5h9v8h-9zM11.5 8h3l2.5 3v2h-5.5" /><circle cx="5.5" cy="14.5" r="1.4" /><circle cx="14" cy="14.5" r="1.4" /></>}
      {kind === 'StockArrived' && <><path d="m10 2.5 7 3.5v8l-7 3.5L3 14V6z" /><path d="m3 6 7 3.5L17 6M10 9.5v8" /></>}
      {/* An open padlock: what was put by is not put by any more (D-198). */}
      {kind === 'HoldReleased' && <><rect x="4.5" y="9" width="11" height="7.5" rx="1.5" /><path d="M7 9V6.5a3 3 0 0 1 5.8-1" /></>}
    </svg>
  )
}

/**
 * The bell (D-128): what the shop or the warehouse should know — a request to answer,
 * stock on its way, a consignment received — with the unread count, and each notice a
 * way straight to the thing it is about.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const notifications = useNotifications()
  const read = useReadNotification()
  const readAll = useReadAllNotifications()
  const unread = notifications.data?.unread ?? 0
  const items = notifications.data?.items ?? []

  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  function follow(item: NotificationItem) {
    setOpen(false)
    if (!item.isRead) read.mutate(item.id)
    if (item.link) navigate(item.link)
  }

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-label={unread > 0 ? `Notifications: ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-dpl-muted hover:bg-dpl-bg hover:text-dpl-text"
      >
        <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 3a4.5 4.5 0 0 1 4.5 4.5v3l1.5 2.5H4l1.5-2.5v-3A4.5 4.5 0 0 1 10 3z" />
          <path d="M8.25 15.5a1.75 1.75 0 0 0 3.5 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-dpl-danger px-1 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-dpl-border bg-dpl-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-dpl-border px-4 py-2.5">
            <p className="text-sm font-semibold">Notifications</p>
            {unread > 0 && (
              <button type="button" className="text-xs font-medium text-dpl-primary hover:underline" onClick={() => readAll.mutate()}>Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-dpl-muted">Nothing yet. Stock requests and consignments show up here.</p>
          ) : (
            <ul className="max-h-[70vh] divide-y divide-dpl-border overflow-y-auto">
              {items.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => follow(item)} className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-dpl-bg ${item.isRead ? '' : 'bg-dpl-primary/[0.04]'}`}>
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tones[item.kind]}`}><Glyph kind={item.kind} /></span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${item.isRead ? 'font-medium' : 'font-semibold'}`}>{item.title}</span>
                      <span className="block text-xs text-dpl-muted">{item.body}</span>
                      <span className="block text-[11px] text-dpl-muted">{formatDateTime(item.raisedAt)}</span>
                    </span>
                    {!item.isRead && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-dpl-primary" aria-label="Unread" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
